import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaScroll,
  FaChartPie,
  FaSignInAlt,
  FaArrowAltCircleLeft,
  FaBook,
  FaUserGraduate,
  FaCog,
  FaClipboardCheck,
  FaEnvelope,
} from "react-icons/fa";
import axios from "axios";
import "./style.css";
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../../js/api.js';
import { carregarAcompanhamentosDoUsuario } from '../../pages/Acompanhamento/services/carregarAcompanhamentos.js';

const getProgramaVinculado = (pathname = "") => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    let userObj = null;
    try {
      const rawUser = localStorage.getItem("user");
      if (rawUser) userObj = JSON.parse(rawUser);
    } catch (_) {}

    const tipo = decoded.tipo || userObj?.tipo;
    if (tipo === "usuario_ciee") {
      return {
        tipo: 99,
        nome: "Dashboard",
        link: "/dashboard",
        icone: "chart",
        cor: "#9333ea",
      };
    }

    const isEstudanteUser =
      tipo === "estudante" ||
      Number(decoded.acesso_autorizacao ?? userObj?.acesso_autorizacao ?? 0) === 1;

    let rawTipoPrograma =
      userObj?.tipo_programa !== undefined && userObj?.tipo_programa !== null
        ? userObj.tipo_programa
        : decoded.tipo_programa;

    if (isEstudanteUser) {
      if (pathname.startsWith("/tca-empregador")) {
        rawTipoPrograma = 2;
      } else if (
        pathname.startsWith("/tca") &&
        !pathname.startsWith("/tca-configuracoes")
      ) {
        rawTipoPrograma = 1;
      } else if (pathname.startsWith("/tce")) {
        rawTipoPrograma = 0;
      } else {
        const moduloAtivo =
          sessionStorage.getItem("modulo_ativo") ||
          localStorage.getItem("ultimo_modulo_acessado") ||
          "";
        if (moduloAtivo === "tca-empregador") rawTipoPrograma = 2;
        else if (moduloAtivo === "tca") rawTipoPrograma = 1;
        else if (moduloAtivo === "tce") rawTipoPrograma = 0;
      }
    }

    const tipoPrograma =
      rawTipoPrograma !== null &&
      rawTipoPrograma !== undefined &&
      rawTipoPrograma !== ""
        ? Number(rawTipoPrograma)
        : NaN;
    switch (tipoPrograma) {
      case 0:
        return {
          tipo: 0,
          nome: "Estágio (TCE)",
          link: "/tce",
          icone: "user",
          cor: "#0ea5e9",
        };
      case 1:
        return {
          tipo: 1,
          nome: "Aprendizagem (TCA)",
          link: "/tca",
          icone: "book",
          cor: "#16a34a",
        };
      case 2:
        return {
          tipo: 2,
          nome: "Aprendizagem CIEEMG Empregador",
          link: "/tca-empregador",
          icone: "book",
          cor: "#16a34a",
        };
      default:
        return null;
    }
  } catch (e) {
    return null;
  }
};

const getTipoUsuario = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    return jwtDecode(token).tipo;
  } catch {
    return null;
  }
};

const getIsEstudante = () => {
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const u = JSON.parse(rawUser);
      if (
        u?.tipo === "estudante" ||
        Number(u?.acesso_autorizacao ?? 0) === 1 ||
        Number(u?.tipo_acesso ?? 0) === 1 ||
        Number(u?.nivel_acesso ?? 0) === 1 ||
        Number(u?.role_id ?? 0) === 1 ||
        Number(u?.role ?? 0) === 1
      ) {
        return true;
      }
    }
  } catch (_) {}

  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (
      decoded.tipo === "estudante" ||
      Number(decoded.acesso_autorizacao ?? 0) === 1 ||
      Number(decoded.tipo_acesso ?? 0) === 1 ||
      Number(decoded.nivel_acesso ?? 0) === 1 ||
      Number(decoded.role_id ?? 0) === 1 ||
      Number(decoded.role ?? 0) === 1
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const getNivelUsuario = () => {
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const u = JSON.parse(rawUser);
      const n = Number(
        u?.acesso_autorizacao ??
        u?.tipo_acesso ??
        u?.nivel_acesso ??
        u?.role_id ??
        0,
      );
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch (_) {}

  const token = localStorage.getItem("token");
  if (!token) return 0;
  try {
    const decoded = jwtDecode(token);
    const n = Number(
      decoded.acesso_autorizacao ??
      decoded.tipo_acesso ??
      decoded.nivel_acesso ??
      decoded.role_id ??
      0,
    );
    if (Number.isFinite(n)) return n;
    return 0;
  } catch {
    return 0;
  }
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const programa = getProgramaVinculado(location.pathname);
  const tipoUsuario = getTipoUsuario();
  const isEstudante = getIsEstudante();
  const nivelUsuario = getNivelUsuario();
  // Operadores CIEE com autorização na fila de validação (SEOPE=2, SECEX=3, INFOR=4).
  // Estudantes (nível 1) e Empresas (nível 0) não têm permissão e não devem fazer polling.
  const isOperadorCiee =
    tipoUsuario === "usuario_ciee" && !isEstudante && nivelUsuario >= 2;

  const [qtdNovos, setQtdNovos] = useState(0);
  const [qtdErrosEmail, setQtdErrosEmail] = useState(() => {
    try {
      const salvo = localStorage.getItem("erro_email_pendentes");
      return salvo !== null ? Number(salvo) : 4;
    } catch {
      return 4;
    }
  });

  // Escuta atualizações de erros de e-mail e fila de validações em tempo real para sincronizar os badges
  useEffect(() => {
    const handleAtualizacao = (e) => {
      if (e?.detail?.pendentes !== undefined) {
        setQtdErrosEmail(Number(e.detail.pendentes));
      }
    };
    const handleValidacoesAtualizado = (e) => {
      if (e?.detail?.total !== undefined) {
        setQtdNovos(Number(e.detail.total));
      }
    };
    window.addEventListener("erro-email:atualizado", handleAtualizacao);
    window.addEventListener("validacoes:novos-atualizado", handleValidacoesAtualizado);
    return () => {
      window.removeEventListener("erro-email:atualizado", handleAtualizacao);
      window.removeEventListener("validacoes:novos-atualizado", handleValidacoesAtualizado);
    };
  }, []);

  // Polling do badge (apenas para operadores CIEE autorizados: SEOPE, SECEX, INFOR)
  // Estudantes (nível 1) e Empresas (nível 0) recebem 403 Forbidden se chamarem esses endpoints.
  useEffect(() => {
    if (!isOperadorCiee) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const endpoints = [
      `${API_BASE_URL}/tca/validacoes/novos`,
      `${API_BASE_URL}/tce/validacoes/novos`,
      `${API_BASE_URL}/tca-empregador/validacoes/novos`,
    ];

    const buscarNovos = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      Promise.allSettled(
        endpoints.map((url) =>
          axios
            .get(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
              const lista = Array.isArray(r.data) ? r.data : [];
              try {
                if (typeof window !== "undefined") {
                  window.__cieeValidacoesNovosCache =
                    window.__cieeValidacoesNovosCache || {};
                  window.__cieeValidacoesNovosCache[url] = {
                    ts: Date.now(),
                    data: r.data,
                  };
                }
              } catch (_) {}
              return lista;
            }),
        ),
      ).then((results) => {
        const total = results.reduce((acc, r) => {
          if (r.status === "fulfilled" && Array.isArray(r.value)) {
            return acc + r.value.length;
          }
          return acc;
        }, 0);
        setQtdNovos(total);
      });
    };

    buscarNovos();

    // Prefetch silencioso da tela de Acompanhamento em background enquanto o usuário está no Dashboard
    const timerPrefetch = setTimeout(() => {
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/acompanhamento") &&
        !window.__cieeAcompanhamentosPreloaded
      ) {
        carregarAcompanhamentosDoUsuario(token, 1, 50, {}).catch(() => {});
      }
    }, 350);

    const interval = setInterval(buscarNovos, 45000);
    return () => {
      clearTimeout(timerPrefetch);
      clearInterval(interval);
    };
  }, [isOperadorCiee]);

  const handleLogout = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch (_) {}
    window.location.href = "/";
  };

  const handleProgramaClick = (e) => {
    if (!programa) return;
    e.preventDefault();
    if (isEstudante) {
      const urlId = new URLSearchParams(location.search).get("id");
      const storedId =
        (programa.tipo === 2
          ? localStorage.getItem("estudante_tca_empregador_id")
          : programa.tipo === 1
            ? localStorage.getItem("estudante_tca_id")
            : localStorage.getItem("estudante_tce_id")) ||
        urlId ||
        localStorage.getItem("estudante_tca_empregador_id") ||
        localStorage.getItem("estudante_tca_id") ||
        localStorage.getItem("estudante_tce_id");
      if (storedId) {
        navigate(`${programa.link}?id=${encodeURIComponent(storedId)}`);
        return;
      }
    }
    navigate(programa.link);
  };

  const renderIcone = () => {
    if (!programa) return null;
    if (programa.icone === "user") return <FaUserGraduate size={25} />;
    if (programa.icone === "book") return <FaBook size={25} />;
    return <FaScroll size={25} />;
  };

  const noValidacoes = location.pathname.startsWith("/validacoes");

  return (
    <nav className="menu-v" id="menu-v">
      <div className="div-logo-menu">
        <img src="./logociee.png" alt="Logo CIEE" />
      </div>

      <div className="menu-v-option-sidebar">
        <div className="dropdown">
          <button
            className="dropbtn-vertical-sidebar"
            onClick={() => {
              try {
                const tk = localStorage.getItem("token");
                if (tk) {
                  const dec = jwtDecode(tk);
                  if (
                    dec?.tipo === "estudante" ||
                    Number(dec?.acesso_autorizacao ?? 0) === 1
                  ) {
                    const tp = Number(dec?.tipo_programa ?? 0);
                    navigate(tp === 2 ? "/tca-empregador" : "/tce");
                    return;
                  }
                  if (dec?.tipo === "empresa" || dec?.tipo === "usuario_externo") {
                    const tp = Number(dec?.tipo_programa ?? 0);
                    const rotaPrograma = tp === 2 ? "/tca-empregador" : tp === 1 ? "/tca" : "/tce";
                    navigate(rotaPrograma);
                    return;
                  }
                }
              } catch (_) {
                /* fallback */
              }
              navigate("/dashboard");
            }}
          >
            <span className="icon">
              <FaArrowAltCircleLeft size={25} />
            </span>
            <span className="txt-link-sidebar">Home</span>
          </button>
        </div>

        {programa && programa.tipo !== 99 && (
          <div className="dropdown">
            <button
              className="dropbtn-vertical-sidebar programa-atual"
              onClick={handleProgramaClick}
              style={{ borderLeft: `4px solid ${programa.cor}` }}
            >
              <span className="icon" style={{ color: programa.cor }}>
                {renderIcone()}
              </span>
              <span className="txt-link-sidebar">
                {isEstudante ? `Meu Contrato (${programa.nome})` : programa.nome}
              </span>
            </button>
          </div>
        )}

        <div className="dropdown">
          <button
            className={`dropbtn-vertical-sidebar ${location.pathname === "/historico-eventos" ? "sidebar-item-ativo" : ""
              }`}
            onClick={() => navigate("/historico-eventos")}
          >
            <span className="icon">
              <FaScroll size={25} />
            </span>

            <span className="txt-link-sidebar">Histórico</span>
          </button>
        </div>

        {/* Acompanhamento: visível para todos os perfis logados, incluindo
            o estudante. A página identifica o nível do usuário e lista
            as autorizações vinculadas ao CPF do estudante quando este é
            o caso. */}
        <div className="dropdown">
          <button
            className={`dropbtn-vertical-sidebar ${
              location.pathname.startsWith("/acompanhamento")
                ? "active sidebar-item-ativo"
                : ""
            }`}
            onClick={() => {
              if (location.pathname.startsWith("/tce")) {
                try {
                  sessionStorage.setItem("modulo_ativo", "tce");
                  localStorage.setItem("ultimo_modulo_acessado", "tce");
                  localStorage.setItem("origem_modulo", "tce");
                } catch (_) {}
              } else if (location.pathname.startsWith("/tca-empregador")) {
                try {
                  sessionStorage.setItem("modulo_ativo", "tca-empregador");
                  localStorage.setItem("ultimo_modulo_acessado", "tca-empregador");
                  localStorage.setItem("origem_modulo", "tca-empregador");
                } catch (_) {}
              } else if (location.pathname.startsWith("/tca")) {
                try {
                  sessionStorage.setItem("modulo_ativo", "tca");
                  localStorage.setItem("ultimo_modulo_acessado", "tca");
                  localStorage.setItem("origem_modulo", "tca");
                } catch (_) {}
              }
              navigate("/acompanhamento");
            }}
          >
            <span className="icon">
              <FaChartPie size={25} />
            </span>
            <span className="txt-link-sidebar">Acompanhamento</span>
          </button>
        </div>

        {isOperadorCiee && (
          <div className="dropdown sidebar-dropdown">
            <button
              className={`dropbtn-vertical-sidebar sidebar-dropdown-btn ${noValidacoes ? "sidebar-dropdown-ativo" : ""}`}
              onClick={() => {
                navigate("/validacoes");
              }}
              style={{ position: "relative" }}
            >
              <span className="icon">
                <FaClipboardCheck size={25} />
              </span>
              <span className="txt-link-sidebar">Validações</span>
              {qtdNovos > 0 && (
                <span className="badge-sidebar">{qtdNovos}</span>
              )}

            </button>
          </div>
        )}

        {isOperadorCiee && (
          <div className="dropdown sidebar-dropdown">
            <button
              className={`dropbtn-vertical-sidebar sidebar-dropdown-btn ${
                location.pathname === "/erro-email" ? "active sidebar-item-ativo" : ""
              }`}
              onClick={() => navigate("/erro-email")}
            >
              <span className="icon">
                <FaEnvelope size={25} />
              </span>
              <span className="txt-link-sidebar">Erro Email</span>
              {qtdErrosEmail > 0 && (
                <span className="badge-sidebar">{qtdErrosEmail}</span>
              )}
            </button>
          </div>
        )}

        {isOperadorCiee && (
          <div className="dropdown">
            <button
              className={`dropbtn-vertical-sidebar ${
                location.pathname === "/tca-configuracoes"
                  ? "active sidebar-item-ativo"
                  : ""
              }`}
              onClick={() => navigate("/tca-configuracoes")}
            >
              <span className="icon">
                <FaCog size={25} />
              </span>
              <span className="txt-link-sidebar">Configurações</span>
            </button>
          </div>
        )}
      </div>

      <div className="menu-option-logout">
        <div className="dropdown">
          <button className="dropbtn-vertical-sidebar" onClick={handleLogout}>
            <span className="icon icon-sair">
              <FaSignInAlt size={25} />
            </span>
            <span className="txt-link-sidebar">Sair</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
